import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { FleetStatistics } from '@routes/sidebar/admin-center/___shared___/dashboards/types.ts';
import type { CommonTableRequest, Pathway, PathwaySummary } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    PathwayToWatchlistParams,
    PathwayWatchlistParams,
    PathwayWatchlistVesselSummaryParams,
    VesselSummary,
    WatchlistPathwayOrderParams,
} from '@routes/sidebar/admin-center/fleet-dashboard/___pages___/learning-dashboard/api/learningDashboard.api.ts';
import {
    getApiPathways,
    getApiPathwayWatchlist,
    getApiPathwayWatchlistVesselSummary,
    getFleetStatistics as getApiFleetStatistics,
    postApiPathwayToWatchlist,
    updateApiWatchlistPathwayOrder,
} from '@routes/sidebar/admin-center/fleet-dashboard/___pages___/learning-dashboard/api/learningDashboard.api.ts';
import { FLEET_STATISTICS_INITIAL_STATE } from '@routes/sidebar/admin-center/fleet-dashboard/___pages___/learning-dashboard/model/constants.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

interface MandatoryLearningStatusError {
    message?: string;
    [k: string]: any;
}

interface MandatoryLearningStatusState {
    error: MandatoryLearningStatusError | null;
    fleetStatistics: FleetStatistics;
    pathwayVesselSummaries: Record<number, PaginatedResponse<VesselSummary, number> | undefined>;
    pathwaySummaries: PaginatedResponse<PathwaySummary, EmptyResultsDescription> | null;
    pathways: Pathway[];
    vesselsSummaryPathwayId: number | null;
    isGetPathwaysPending: boolean;
    isGetFleetStatisticsPending: boolean;
    isGetPathwayWatchlistPending: boolean;
    isGetVesselsSummaryPending: boolean;
    isAddPathwayToWatchlistPending: boolean;
    isUpdateWatchlistPathwayOrderPending: boolean;
    isPathwaysModalOpened: boolean;
    selectedCrewStatusPillFilter: string | null; // will be sent as { filterName: ['True'|'False'] }
}

const initialState: MandatoryLearningStatusState = {
    error: null,
    fleetStatistics: FLEET_STATISTICS_INITIAL_STATE,
    pathwaySummaries: null,
    pathwayVesselSummaries: [],
    pathways: [],
    vesselsSummaryPathwayId: null,
    isGetPathwaysPending: false,
    isGetFleetStatisticsPending: false,
    isGetPathwayWatchlistPending: false,
    isGetVesselsSummaryPending: false,
    isAddPathwayToWatchlistPending: false,
    isUpdateWatchlistPathwayOrderPending: false,
    isPathwaysModalOpened: false,
    selectedCrewStatusPillFilter: null,
};

export const getFleetStatistics = createAsyncThunk<
    FleetStatistics,
    { tenantAlias: string; payload: CommonTableRequest },
    { rejectValue: MandatoryLearningStatusError }
>('mandatoryLearningStatus/getFleetStatistics', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiFleetStatistics({ tenantAlias, payload }).catch((err) => rejectWithValue(err)),
);

export const getPathwayWatchlist = createAsyncThunk<
    PaginatedResponse<PathwaySummary, EmptyResultsDescription>,
    PathwayWatchlistParams,
    { rejectValue: MandatoryLearningStatusError }
>('mandatoryLearningStatus/getPathwayWatchlist', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiPathwayWatchlist({
        tenantAlias,
        payload,
    })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getPathwayWatchlistVesselSummary = createAsyncThunk<
    PaginatedResponse<VesselSummary, number>,
    PathwayWatchlistVesselSummaryParams,
    { rejectValue: MandatoryLearningStatusError }
>(
    'mandatoryLearningStatus/getPathwayWatchlistVesselSummary',
    ({ tenantAlias, pathwayId, payload }, { rejectWithValue }) =>
        getApiPathwayWatchlistVesselSummary({
            tenantAlias,
            pathwayId,
            payload,
        })
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const getPathways = createAsyncThunk<
    Pathway[],
    { tenantAlias: string },
    { rejectValue: MandatoryLearningStatusError }
>('mandatoryLearningStatus/getPathways', ({ tenantAlias }, { rejectWithValue }) =>
    getApiPathways({
        tenantAlias,
    })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const addPathwayToWatchlist = createAsyncThunk<
    void,
    PathwayToWatchlistParams,
    { rejectValue: MandatoryLearningStatusError }
>('mandatoryLearningStatus/addPathwayToWatchlist', ({ tenantAlias, pathwayId }, { rejectWithValue }) =>
    postApiPathwayToWatchlist({
        tenantAlias,
        pathwayId,
    })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateWatchlistPathwayOrder = createAsyncThunk<
    void,
    WatchlistPathwayOrderParams,
    { rejectValue: MandatoryLearningStatusError }
>(
    'mandatoryLearningStatus/updateWatchlistPathwayOrder',
    ({ tenantAlias, pathwayId, order, isDeleted }, { rejectWithValue }) =>
        updateApiWatchlistPathwayOrder({
            tenantAlias,
            pathwayId,
            order,
            isDeleted,
        })
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const mandatoryLearningStatusSlice = createSlice({
    name: 'mandatoryLearningStatus',
    initialState,
    reducers: {
        setIsPathwaysModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isPathwaysModalOpened = action.payload;
        },
        setReorderedPathways: (state, action: PayloadAction<PathwaySummary[]>) => {
            if (state.pathwaySummaries?.data) {
                state.pathwaySummaries.data = action.payload;
            }
        },
        setSelectedCrewStatusPillFilter: (state, action: PayloadAction<string | null>) => {
            state.selectedCrewStatusPillFilter = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getFleetStatistics.pending, (state) => {
            state.isGetFleetStatisticsPending = true;
            state.error = null;
        });

        builder.addCase(getFleetStatistics.fulfilled, (state, action) => {
            state.isGetFleetStatisticsPending = false;
            state.fleetStatistics = action.payload;
        });

        builder.addCase(getFleetStatistics.rejected, (state, action) => {
            state.isGetFleetStatisticsPending = false;
            state.error = extractError(action);
        });

        // Watchlist
        builder.addCase(getPathwayWatchlist.pending, (state) => {
            state.isGetPathwayWatchlistPending = true;
        });
        builder.addCase(getPathwayWatchlist.fulfilled, (state, action) => {
            state.isGetPathwayWatchlistPending = false;
            state.pathwaySummaries = action.payload;
        });
        builder.addCase(getPathwayWatchlist.rejected, (state, action) => {
            state.isGetPathwayWatchlistPending = false;
            state.error = extractError(action);
        });

        // Vessel summary
        builder.addCase(getPathwayWatchlistVesselSummary.pending, (state, action) => {
            state.vesselsSummaryPathwayId = action.meta.arg.pathwayId;
            state.isGetVesselsSummaryPending = true;
        });
        builder.addCase(getPathwayWatchlistVesselSummary.fulfilled, (state, action) => {
            state.isGetVesselsSummaryPending = false;
            state.vesselsSummaryPathwayId = null;

            const pathwayId = action.payload.resultsDescription;

            state.pathwayVesselSummaries[pathwayId] = action.payload;
        });
        builder.addCase(getPathwayWatchlistVesselSummary.rejected, (state, action) => {
            state.isGetVesselsSummaryPending = false;
            state.vesselsSummaryPathwayId = null;

            state.error = extractError(action);
        });

        // Pathways
        builder.addCase(getPathways.pending, (state) => {
            state.isGetPathwaysPending = true;
        });
        builder.addCase(getPathways.fulfilled, (state, action) => {
            state.isGetPathwaysPending = false;
            state.pathways = action.payload;
        });
        builder.addCase(getPathways.rejected, (state, action) => {
            state.isGetPathwaysPending = false;
            state.error = extractError(action);
        });

        // Add pathway to watchlist
        builder.addCase(addPathwayToWatchlist.pending, (state) => {
            state.isAddPathwayToWatchlistPending = true;
        });
        builder.addCase(addPathwayToWatchlist.fulfilled, (state) => {
            state.isAddPathwayToWatchlistPending = false;
        });
        builder.addCase(addPathwayToWatchlist.rejected, (state, action) => {
            state.isAddPathwayToWatchlistPending = false;
            state.error = extractError(action);
        });

        // Update watchlist pathway order
        builder.addCase(updateWatchlistPathwayOrder.pending, (state) => {
            state.isUpdateWatchlistPathwayOrderPending = true;
        });

        builder.addCase(updateWatchlistPathwayOrder.fulfilled, (state) => {
            state.isUpdateWatchlistPathwayOrderPending = false;
        });

        builder.addCase(updateWatchlistPathwayOrder.rejected, (state, action) => {
            state.isUpdateWatchlistPathwayOrderPending = false;
            state.error = extractError(action);
        });
    },
});

export const { setIsPathwaysModalOpened, setReorderedPathways, setSelectedCrewStatusPillFilter } =
    mandatoryLearningStatusSlice.actions;

export default mandatoryLearningStatusSlice.reducer;
